import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import LinkTo from '../forms/LinkTo';
import { IsAuthorized } from '../RoleProtected';
import { SCREEN_MAX_WIDTH, SCREEN_WIDTH, useGlobalState } from '../stateProvider/StateProvider';
import { BorderLessButton } from '../forms/Button';
import { useLocation } from 'react-router-dom';

const Wrapper = styled.div`
    display: flex;
    align-items: flex-start;
    width: 100%;
    height: inherit;
    flex-direction: ${({ row }) => row ? 'column' : 'row'};
    gap: 1em;
    height: auto;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        flex-direction: column;
    }
`;
const Border = styled.div`
    ${({ rowedTabs, theme }) => rowedTabs ? `
        width: 100%;
        border-bottom: 1px solid ${theme.gray}40;
    ` : `
        height: 100%;
        min-height: 70svh;
        border-right: 1px solid ${theme.gray}40;
    `}

    
`
const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    animation: fadeIn;
    animation-duration: 0.5s;
    animation-fill-mode: forwards;

    // height: inherit;
    // TODO MONITOR THIS

`;

const TabWrapper = styled.div`
    display: flex;
    flex-direction: ${({ row }) => row ? 'row' : 'column'};
    align-items: flex-start;
    min-width: 15%;
    position: sticky;
    top: 4.6em;
    gap: 1.5em;
    min-height: 2em;

    ${({ row, theme }) => row ? `
        width: 100%;
        overflow: auto;
        flex-direction: row;
        align-items: center;
        top: 3.2em;
        padding: 0.5em 0 0.5em 1em;
        overflow-x: auto;
        overflow-y: hidden;
        z-index: 10;
        white-space: nowrap;
        background: ${theme.background};
        scroll-behavior: smooth;
        overscroll-behavior: contain;
    ` : ''}

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        width: 100%;
        overflow: auto;
        flex-direction: row;
        align-items: center;
        top: -1.5em;
        padding: 0.5em 0;
        background: ${({ theme }) => theme.background};
        overflow-x: auto;
        overflow-y: hidden;
        z-index: 1;
        min-height: 3.5em;
    }
`;
const TabHeading = styled.div`
    color: ${({ theme }) => theme.text};
    display: flex;
    gap: 0.5em;
    cursor: pointer;
    font-weight: ${({ active }) => active ? 'bold' : 'unset'};
    transform: ${({ active, row }) => row ? `translateY(${active ? '-0.1em' : ''})` : `translateX(${active ? '0.7em' : ''})`};
    transition: background 0.2s, transform 0.1s;
    z-index: 1;

    // background: ${({ active, theme }) => active ? (theme.gray + '40') : '000'};
    // border-radius: 2em;

    max-width: ${({ row, width }) => row ? '' : (width * .9) + 'px'};

    &:hover {
        transform: ${({ row }) => row ? 'translateY(-0.1em)' : 'translateX(0.7em)'};
    }

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        padding: 0.5em 1.5em;
        text-align: center;
        transform: unset !important;
    }
`;

const Link = styled(LinkTo)`
    overflow: hidden;
    position: relative;
    display: inline-block;
    margin: 0 5px 0 5px;
    text-align: center;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        white-space: nowrap;
    }
`;

const FlipTab = styled(BorderLessButton)`
    color: ${({ theme }) => theme.text}80;
    transform: unset;
    padding: 0 1em;

    &:hover {
        background-color: unset;
        border-color: unset;
        color: ${({ theme }) => theme.text};
        transform: unset;
        box-shadow: unset;
    }
    &:active {
        background-color: unset;
    }

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        display: none;
    }
`;

const Highlighter = styled.div`
    position: absolute;
    width: 50px;
    opacity: 0;
    // border-bottom: 2px solid ${({ theme }) => theme.secondary};
    background: ${({ theme }) => theme.gray}40;
    border-radius: 2em;
    transition: all 0.3s cubic-bezier(0, 0, 0, 1);
    z-index: 0;
`

const Tabbed = ({ children = [], ...props }) => {
    const {
        state: {
            rowedTabs,
        },
        toggleRowTab
    } = useGlobalState();

    const location = useLocation();

    const firstTab = useRef();
    const horScroll = useRef();

    if (!(children instanceof Array)) {
        children = [children]
    }

    const getTabIndex = () => {
        const cleanedHash = window.location.hash.slice(1);
        if (!cleanedHash) return 0;
        return children.findIndex(e => e.props.tab.replace(/ /g, '').toUpperCase() === cleanedHash.toUpperCase())
    }

    const [tabIndex, setTabIndex] = useState(getTabIndex());
    const [headingPos, setHeadingPos] = useState({ x: 0, y: 0 });
    const [tabWidth, setTabWidth] = useState(100);

    useLayoutEffect(() => {
        setTabWidth(horScroll.current.getBoundingClientRect().width);

        const handleResize = () => setTimeout(() => setHeadingPos(getPos(firstTab)), 200)
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useLayoutEffect(() => {
        if (getTabIndex() !== tabIndex)
            setTabIndex(getTabIndex());
        setTimeout(() => setHeadingPos(getPos(firstTab)), 100);
    }, [location.pathname, location.hash]);

    const getPos = (element) => {
        if (!horScroll.current) return null;

        const wrap = horScroll.current.getBoundingClientRect();
        let ele = null;
        if (element.current) {
            ele = element.current.getBoundingClientRect();
        } else if (element.target) {
            ele = element.target.getBoundingClientRect();
        } else {
            return null;
        }
        const result = {};
        for (let key in wrap) result[key] = ele[key] - wrap[key];
        result.width = ele.width;
        result.height = ele.height;
        return result;
    }

    return (
        <Wrapper row={rowedTabs}>
            <TabWrapper
                ref={horScroll}
                row={rowedTabs}
                onWheel={(e) => horScroll.current.scrollLeft += e.deltaY * 2}
            >
                {
                    children.map((e, i) => {
                        const roles = e.props.roles
                        const tab = e.props.tab

                        if (roles) {
                            if (!IsAuthorized(roles)) {
                                if (tabIndex === i) setTabIndex(i + 1);
                                return null
                            };
                        }

                        return (
                            <TabHeading
                                ref={tabIndex === i ? firstTab : null}
                                key={`TabHeading-${i}`}
                                onClick={() => setTabIndex(i)}
                                active={tabIndex === i}
                                row={rowedTabs}
                                width={tabWidth}
                                title={tab}
                            >
                                <Link
                                    to={`#${tab.replace(/ /g, '')}`}
                                    inherit
                                >
                                    {tab}
                                </Link>
                            </TabHeading>
                        )
                    })
                }

                <FlipTab
                    row={rowedTabs}
                    icon={rowedTabs ? 'arrowTurnDown' : 'arrowTurnUp'}
                    onClick={() => {
                        toggleRowTab()
                        setTimeout(() => {
                            setHeadingPos(getPos(firstTab))
                        }, 200)
                    }}
                    aria-label='Flip Tabs'
                />
                {!SCREEN_WIDTH && headingPos && (
                    <Highlighter
                        style={{
                            top: `calc(${headingPos.y}px - 0.15em)`,
                            left: `calc(${headingPos.x}px - 0.7em)`,
                            width: `calc(${headingPos.width}px + 1.5em)`,
                            height: `calc(${headingPos.height}px + 0.3em)`,
                            opacity: 1,
                        }}
                    />
                )}
            </TabWrapper>

            {!SCREEN_WIDTH && <Border rowedTabs={rowedTabs} />}

            {children[tabIndex] ? <ContentWrapper key={`tab-Child-${tabIndex}`}>{children[tabIndex]}</ContentWrapper> : null}
        </Wrapper>
    );
}

export default Tabbed;
